import { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Button } from 'primereact/button';
import { Message } from 'primereact/message';
import { useLanguage } from '../../../contexts/LanguageContext';
import { selectUserParams } from '../../../store/slices/userParamsSlice';
import { usePermissions } from '../../../hooks/usePermissions';
import GenericFileExportButton from '../../../components/common/GenericFileExportButton';

import useTechnicalSupportRequestData from './hooks/useTechnicalSupportRequestData';
import TechnicalSupportRequestForm from './components/TechnicalSupportRequestForm';
import TechnicalSupportRequestTable from './components/TechnicalSupportRequestTable';
import { useExportTechnicalSupportRequestToExcelMutation } from '../../../api/endpoints/technicalSupportRequest';

const TechnicalSupportRequestPage = () => {
    const { th } = useLanguage();
    const userParams = useSelector(selectUserParams);
    const { hasAdminRole, hasDealerRole, hasServiceDealerRole, hasSalesDealerRole, hasWarrantyTeamRole, hasKarsanSSHRole, hasSSHManagerRole, hasSSHDirectorRole, hasSSHGeneralManagerRole, hasWarrantyQualityOfficerRole, hasTechnicalSupportUserRole } = usePermissions();

    const isDealer = hasDealerRole() || hasServiceDealerRole() || hasSalesDealerRole() || hasTechnicalSupportUserRole();
    const isSshUser = hasKarsanSSHRole() || hasSSHManagerRole() || hasSSHDirectorRole() || hasSSHGeneralManagerRole();
    const isKarsanTeam = hasAdminRole() || hasWarrantyTeamRole() || isSshUser || hasWarrantyQualityOfficerRole();

    const dealerId = useMemo(() => userParams?.dealerId ?? userParams?.DealerId ?? null, [userParams]);

    const filterDealerId = useMemo(() => {
        if (isKarsanTeam) {
            return null;
        }
        if (isDealer) {
            return dealerId;
        }
        return dealerId;
    }, [isDealer, isKarsanTeam, dealerId]);

    const { requests, loading, totalCount, lazyParams, onPage, activeFilters, handleFilterChange, initFilters, sortField, sortOrder, onSort, resetCounter, refetch, exportParams } = useTechnicalSupportRequestData(filterDealerId);

    const [dialogVisible, setDialogVisible] = useState(false);
    const [selectedRequest, setSelectedRequest] = useState(null);
    const [mode, setMode] = useState('create');

    const openDialog = useCallback((nextMode, req = null) => {
        setSelectedRequest(req);
        setMode(nextMode);
        setDialogVisible(true);
    }, []);

    const hideDialog = useCallback(() => {
        setDialogVisible(false);
        setSelectedRequest(null);
        setMode('create');
    }, []);

    const handleSaveSuccess = useCallback(() => {
        refetch();
        hideDialog();
    }, [refetch, hideDialog]);

    const selectedRequestIdRef = useRef(null);
    useEffect(() => {
        selectedRequestIdRef.current = selectedRequest?.id ?? null;
    }, [selectedRequest]);

    const handleRefreshRequest = useCallback(() => {
        const reqId = selectedRequestIdRef.current;
        if (!reqId) return;

        refetch().then((result) => {
            if (result.data) {
                const updatedRequest = result.data.items?.find((r) => r.id === reqId);
                if (updatedRequest) {
                    setSelectedRequest(updatedRequest);
                }
            }
        });
    }, [refetch]);

    const canCreateRequest = isDealer ? !!dealerId : isKarsanTeam;

    return (
        <div className="card">
            <div className="flex justify-content-between align-items-center mb-4">
                <h1 className="m-0">{th('technicalSupport.pageTitle')}</h1>

                <div className="flex gap-2">
                    <GenericFileExportButton
                        useExportQuery={useExportTechnicalSupportRequestToExcelMutation}
                        exportParams={exportParams}
                        buttonLabel={th('common.export') || 'Excel'}
                        buttonClassName="p-button-success"
                        icon="pi pi-file-excel"
                        fileNameExtractor={(result) => result?.fileName || 'teknik_destek_talepleri.zip'}
                        dataExtractor={(result) => result?.fileBase64}
                        contentTypeExtractor={(result) => result?.contentType}
                    />

                    {(isDealer || isKarsanTeam) && <Button label={th('technicalSupport.addNewRequest')} icon="pi pi-plus" onClick={() => openDialog('create')} disabled={!canCreateRequest} />}
                </div>
            </div>

            <TechnicalSupportRequestTable
                requests={requests}
                loading={loading}
                totalCount={totalCount}
                lazyParams={lazyParams}
                onPage={onPage}
                activeFilters={activeFilters}
                onFiltersChange={handleFilterChange}
                onResetFilters={initFilters}
                sortField={sortField}
                sortOrder={sortOrder}
                onSortChange={onSort}
                resetCounter={resetCounter}
                onEdit={(req) => openDialog('edit', req)}
            />

            <TechnicalSupportRequestForm
                dialogVisible={dialogVisible}
                editMode={mode === 'edit'}
                mode={mode}
                hideDialog={hideDialog}
                request={selectedRequest}
                onSaveSuccess={handleSaveSuccess}
                onRefreshRequest={handleRefreshRequest}
                dealerId={dealerId}
                requireDealerAddressSelection={isKarsanTeam}
            />
        </div>
    );
};

export default TechnicalSupportRequestPage;
